/**
 * Service layer — Construction · Value Engineering (Supabase-backed)
 *
 * Wraps /api/value-engineering (Clerk-authed, service_role). Migrated from the Airtable
 * "Project Tuning" tables. An effort is a per-project VE round; items are the cost-saving
 * proposals. Savings rollups are derived here, not stored. Components call only these
 * functions — never supabase.from(...) directly.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/value-engineering' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Value Engineering request failed: ' + res.status)
  }
  return res.json()
}

/* ── Display constants ──────────────────────────────────────────────────────── */

export const VE_STATUS_LABELS = {
  gathering:     'Gathering Ideas',
  under_review:  'Under Review',
  complete:      'Complete',
}
export const VE_DISPOSITION_LABELS = {
  drafting:     'Drafting',
  under_review: 'Under Review',
  accepted:     'Accepted',
  declined:     'Declined',
}

/** Sum of all item estimated savings on an effort (expects effort.ve_items). */
export function sumEstimatedSavings(effort) {
  return (effort?.ve_items || []).reduce((s, i) => s + Number(i.estimated_savings || 0), 0)
}
/** Sum of estimated savings on accepted items only. */
export function sumAcceptedSavings(effort) {
  return (effort?.ve_items || []).filter(i => i.disposition === 'accepted')
    .reduce((s, i) => s + Number(i.estimated_savings || 0), 0)
}
/** Baseline minus accepted savings (the budget with only approved items). */
export function revisedEstimate(effort) {
  const base = Number(effort?.baseline_estimate || 0)
  return base ? base - sumAcceptedSavings(effort) : null
}

/* ── Reads ────────────────────────────────────────────────────────────────── */

/** All VE efforts for a project, each with its items (ve_items). */
export function getProjectEfforts(projectId) {
  if (!projectId) return Promise.resolve([])
  return call('GET', null, 'projectId=' + encodeURIComponent(projectId)).then(d => d.efforts || [])
}

/** One effort with its items: { effort, items }. */
export function getEffortDetail(effortId) {
  return call('GET', null, 'effortId=' + encodeURIComponent(effortId))
}

/** Efforts mounted at a milestone, each with items. */
export function getEffortsForMilestone(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve([])
  return call('GET', null, 'milestoneId=' + encodeURIComponent(milestoneAirtableId)).then(d => d.efforts || [])
}

/* ── Writes ───────────────────────────────────────────────────────────────── */

export function createEffort(projectId, fields, milestoneAirtableId) {
  return call('POST', { action: 'createEffort', projectId, fields, milestoneAirtableId }).then(d => d.effort)
}
export function updateEffort(effortId, fields) {
  return call('POST', { action: 'updateEffort', effortId, fields }).then(d => d.ok)
}
export function deleteEffort(effortId) {
  return call('POST', { action: 'deleteEffort', effortId }).then(d => d.ok)
}
export function createItem(effortId, fields) {
  return call('POST', { action: 'createItem', effortId, fields }).then(d => d.item)
}
export function updateItem(itemId, fields) {
  return call('POST', { action: 'updateItem', itemId, fields }).then(d => d.ok)
}
export function deleteItem(itemId) {
  return call('POST', { action: 'deleteItem', itemId }).then(d => d.ok)
}
export function linkMilestone(effortId, milestoneAirtableId, role = 'primary') {
  return call('POST', { action: 'linkMilestone', effortId, milestoneAirtableId, role }).then(d => d.ok)
}
export function unlinkMilestone(effortId, milestoneAirtableId) {
  return call('POST', { action: 'unlinkMilestone', effortId, milestoneAirtableId }).then(d => d.ok)
}
